import { useRef, useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import DocumentCamera from "../../reusable/DocumentCamera";
import IdFrame from "../../reusable/frames/IdFrame";
import PassportFrame from "../../reusable/frames/PassportFrame";
import NinSlipFrame from "../../reusable/frames/NinSlipFrame";
import { Button } from "@nextui-org/react";
import { CameraIcon2 } from "../../icons/CameraIcon2";
import Header from "../../reusable/Header";
import { validateProcess } from "../../../store/verification.slice";
import { convertToJpeg, IS_DEV } from "../../utils";
import UploadSuccess from "../../reusable/UploadSuccess";
import CountryLabel from "../../reusable/CountryLabel";
import { IdIcon } from "../../icons/IdIcon";
import PoweredBy from "../../reusable/PoweredBy";
import UploadError from "../../reusable/UploadError";
import DocUpLoadingPage from "../../reusable/DocUpLoadingPage";
import { useSelector } from "react-redux";
import { trackEvent } from "../../../utils/analytics";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const CAPTURE_TIP_KEYS = ["flatSurface", "textVisible", "avoidGlare"];

function DocumentCameraPrompt({
  title,
  country,
  side,
  type,
  fieldName,
  inputChange,
  illustration,
  goback,
  restrictGallery,
  verification,
  process,
}) {
  const { t } = useTranslation();
  const [takePicture, setTakePicture] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingError, setUploadingError] = useState("");
  const [capturedImage, setCapturedImage] = useState(null);
  const fileInputRef = useRef(null);
  const { credentials } = useSelector((store) => store.verification);

  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    return () => {
      if (capturedImage) URL.revokeObjectURL(capturedImage);
    };
  }, [capturedImage]);

  const onCapture = (imageSrc) => {
    // imageSrc is a base64 JPEG with EXIF already injected by DocumentCamera.
    // Convert directly to Blob via atob — canvas.toBlob would strip the EXIF.
    const byteStr = atob(imageSrc.split(",")[1]);
    const ab = new ArrayBuffer(byteStr.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
    const blob = new Blob([ab], { type: "image/jpeg" });
    setCapturedImage(URL.createObjectURL(blob));
    validateImage(blob);
  };

  const validateImage = (generatedBlob) => {
    setUploading(true);
    const docName = `${verification?.verificationId}-image-${side}.jpeg`;

    validateProcess(
      process,
      [{ name: docName, blob: generatedBlob }],
      [
        {
          fileName: docName,
          fieldName: fieldName,
          page: side,
          type: type,
          country: country?.code,
          value: docName,
        },
      ],
      params.get("clientId"),
      verification?.verificationId,
      credentials?.accessToken
    )
      .then((_) => {
        setUploading(false);
        setUploadingError("");
        trackEvent("document_side_captured", {
          flowId: verification?.flowId,
          verificationId: verification?.verificationId,
          side,
          documentType: type,
          country: country?.code,
        });
        inputChange({
          document: { name: docName, blob: generatedBlob },
          field: {
            fieldName,
            fileName: docName,
            value: docName,
            page: side,
            type,
            country: country?.code,
          },
        });
      })
      .catch((err) => {
        setUploading(false);
        let message;
        if (axios.isAxiosError(err)) {
          const responseData = err.response?.data;
          message = Array.isArray(responseData)
            ? responseData.map((e) => e?.defaultMessage || t("documentUpload.cameraPrompt.errors.unknownError")).join("\n")
            : t("documentUpload.cameraPrompt.errors.serverError");
        } else {
          message = t("documentUpload.cameraPrompt.errors.networkError");
        }
        trackEvent("document_quality_check_failed", {
          flowId: verification?.flowId,
          verificationId: verification?.verificationId,
          side,
          documentType: type,
          error: message,
        });
        setUploadingError(message);
      });
  };

  const tryAgain = () => {
    setTakePicture(false);
    setUploading(false);
    setUploadingError("");
    setCapturedImage(null);
  };

  const handleAttachFileBtnClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const selectedFile = Array.from(event.target.files)[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      event.target.value = "";
      setUploadingError(t("documentUpload.cameraPrompt.errors.fileTooLarge"));
      return;
    }

    // Convert to JPEG via canvas before uploading. This handles HEIC files from
    // iPhone, where iOS Safari reports the MIME type as "image/jpeg" but the
    // actual bytes are still HEIC, causing the server's magic-byte check to fail.
    convertToJpeg(selectedFile)
      .then((jpegBlob) => {
        setCapturedImage(URL.createObjectURL(jpegBlob));
        validateImage(jpegBlob);
      })
      .catch(() => {
        event.target.value = "";
        setUploadingError(t("documentUpload.cameraPrompt.errors.readError"));
      });
  };

  const sideLabel = side === "FRONT"
    ? t("documentUpload.cameraPrompt.sideLabel.front")
    : t("documentUpload.cameraPrompt.sideLabel.back");

  return (
    <div className="h-full w-full flex flex-col">

      {/* Camera view — full screen */}
      {!capturedImage && takePicture && (
        <DocumentCamera
          onCapture={onCapture}
          documentType={type}
          side={side}
          overlay={type === "PASSPORT" ? PassportFrame : type === "NIN_ID_SLIP" ? NinSlipFrame : IdFrame}
          debug={IS_DEV && params.get("debug") === "true"}
          flowId={verification?.flowId}
          verificationId={verification?.verificationId}
          clientId={params.get("clientId")}
          accessToken={credentials?.accessToken}
        />
      )}

      {/* Prompt view */}
      {!capturedImage && !takePicture && (
        <>
          <Header goBack={goback} />
          <div className="flex flex-col p-5 h-full justify-between">

            {/* Heading */}
            <div className="flex flex-col items-center text-center gap-1">
              <div className="heading text-xl">{title}</div>
              <CountryLabel name={country?.name} flag={country?.flag} code={country?.code} />
              <div className="desc text-sm mt-1">
                <Trans
                  i18nKey="documentUpload.cameraPrompt.positionInstruction"
                  values={{ side: sideLabel.toLowerCase() }}
                  components={{ strong: <strong/> }}
                />
              </div>
            </div>

            {/* Illustration */}
            <div className="flex flex-col items-center gap-2">
              {illustration}
              <div
                className="text-xs font-semibold uppercase tracking-widest"
                style={{color: "var(--urtentic-sdk-body, #737373)"}}
              >
                {sideLabel}
              </div>
            </div>

            {/* Tips + buttons */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5 p-3 rounded-lg background">
                {CAPTURE_TIP_KEYS.map((key) => (
                  <div key={key} className="flex flex-row items-start gap-2">
                    <span className="text-xs mt-0.5" style={{ color: "var(--urtentic-sdk-primary, #255CFF)" }}>✓</span>
                    <span className="desc text-xs leading-relaxed">{t(`documentUpload.cameraPrompt.tips.${key}`)}</span>
                  </div>
                ))}
              </div>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              <Button
                radius="sm"
                startContent={<CameraIcon2 />}
                color="primary"
                size="lg"
                fullWidth
                onClick={() => setTakePicture(true)}
              >
                {t("documentUpload.cameraPrompt.takePhoto")}
              </Button>
              {restrictGallery?.value !== "true" && (
                <Button
                  radius="sm"
                  startContent={<IdIcon />}
                  color="default"
                  fullWidth
                  onClick={handleAttachFileBtnClick}
                >
                  {t("documentUpload.cameraPrompt.chooseFromGallery")}
                </Button>
              )}
              <div className="flex justify-center">
                <PoweredBy />
              </div>
            </div>

          </div>
        </>
      )}

      {/* Uploading state */}
      {uploading && capturedImage && (
        <DocUpLoadingPage imageSrc={capturedImage} label={t("documentUpload.cameraPrompt.checkingDocument")} />
      )}

      {/* Error state */}
      {uploadingError && (
        <UploadError
          tryAgainFunc={tryAgain}
          title={t("documentUpload.cameraPrompt.tryAgainTitle")}
          desc={uploadingError}
          imageSrc={{ type: "image", value: capturedImage }}
        />
      )}

      {/* Success state */}
      {!uploadingError && !uploading && capturedImage && (
        <UploadSuccess message={t("documentUpload.cameraPrompt.captured")} />
      )}

    </div>
  );
}

export default DocumentCameraPrompt;
